import { ArrowLeft, Download, Edit3, Eye, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Customization, Resume, ResumeData } from '../../types';
import LoadingOverlay from '../Common/LoadingOverlay';
import './ResumeEditor.css';
import EditorSidebar from './EditorSidebar';
import LivePreview from './LivePreview';
import SectionReorder from './SectionReorder';
import StyleCustomization from './StyleCustomization';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ResumeEditor: React.FC = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams<{ resumeId: string }>();
  
  const [resume, setResume] = useState<Resume | null>(null);
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [customization, setCustomization] = useState<Customization>({
    sectionOrder: ['personal', 'summary', 'experience', 'projects', 'education', 'skills', 'certifications'],
    hiddenSections: [],
    colors: {},
    fonts: {
      headerFont: 'Arial, sans-serif',
      bodyFont: 'Arial, sans-serif',
      fontSize: 10,
    },
    spacing: {
      lineHeight: 1.5,
      sectionSpacing: 15,
      margins: {
        top: 0.5,
        right: 0.5,
        bottom: 0.5,
        left: 0.5,
      },
    },
  });
  const [previewHTML, setPreviewHTML] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<'edit' | 'preview'>('edit');

  // Debounce resumeData and customization to prevent API flood
  const debouncedResumeData = useDebounce(resumeData, 800);
  const debouncedCustomization = useDebounce(customization, 800);

  const loadResume = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getResume(resumeId!);
      setResume(data.resume);
      setResumeData(data.resume.data);
      
      // Load existing customization if available, ensuring all sections and fonts are present
      if (data.resume.customization) {
        const defaultSections = ['personal', 'summary', 'experience', 'projects', 'education', 'skills', 'certifications'];
        const loadedSections = data.resume.customization.sectionOrder || [];
        
        // Add any missing sections from default to the end of the loaded order
        const missingSections = defaultSections.filter(s => !loadedSections.includes(s));
        const mergedSectionOrder = [...loadedSections, ...missingSections];
        
        // Ensure fonts are properly loaded with defaults
        const defaultFonts = {
          headerFont: 'Arial, sans-serif',
          bodyFont: 'Arial, sans-serif',
          fontSize: 10,
        };
        
        setCustomization({
          ...customization, // Keep defaults
          ...data.resume.customization,
          sectionOrder: mergedSectionOrder,
          fonts: {
            ...defaultFonts,
            ...(data.resume.customization.fonts || {}),
          },
          colors: data.resume.customization.colors || {},
        });
      }
    } catch (err: any) {
      console.error('Failed to load resume:', err);
      setError('Failed to load resume. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPreview = async () => {
    if (!resumeData || !resume) return;
    
    try {
      setIsPreviewLoading(true);
      const html = await api.previewResumeWithCustomization(
        resumeId!,
        resumeData,
        customization
      );
      setPreviewHTML(html);
    } catch (err: any) {
      console.error('Failed to load preview:', err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (resumeId) {
      loadResume();
      
      // Check for optimization suggestions
      const suggestions = sessionStorage.getItem('optimization_suggestions');
      if (suggestions) {
        try {
          const parsed = JSON.parse(suggestions);
          setOptimizationSuggestions(parsed);
          setShowSuggestions(true);
          sessionStorage.removeItem('optimization_suggestions');
        } catch (e) {
          console.error('Failed to parse optimization suggestions:', e);
        }
      }
    }
    // loadResume is defined in this component and we intentionally
    // only want to re-run when resumeId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  useEffect(() => {
    if (debouncedResumeData && resume) {
      loadPreview();
    }
    // loadPreview uses debouncedResumeData, debouncedCustomization, and resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedResumeData, debouncedCustomization, resume]);

  const handleDataChange = (updatedData: ResumeData) => {
    setResumeData(updatedData);
    setHasUnsavedChanges(true);
  };

  const handleCustomizationChange = (updatedCustomization: Customization) => {
    setCustomization(updatedCustomization);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!resumeData || !resumeId) return;

    try {
      setIsSaving(true);
      setError(null);
      
      // Update resume with new data and customization
      await api.updateResumeWithCustomization(resumeId, {
        data: resumeData,
        customization: customization,
      });

      setHasUnsavedChanges(false);
      
      // Show success message (you can add a toast notification here)
      console.log('Resume saved successfully!');
    } catch (err: any) {
      console.error('Failed to save resume:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndDownload = async () => {
    await handleSave();
    if (resumeId) {
      try {
        const blob = await api.downloadResume(resumeId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `resume_${resumeId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Failed to download resume:', err);
        setError('Failed to download resume. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <LoadingOverlay message="Loading resume editor..." />;
  }

  if (error && !resumeData) {
    return (
      <div className="editor-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="resume-editor">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <button
            onClick={() => navigate(`/resume/${resumeId}`)}
            className="btn btn-ghost"
            title="Back"
          >
            <ArrowLeft size={20} />
            <span className="btn-text">Back</span>
          </button>
          <h1>Edit Resume</h1>
          {hasUnsavedChanges && (
            <span className="unsaved-indicator">• Unsaved</span>
          )}
        </div>
        
        <div className="header-actions">
          <button
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
            className="btn btn-secondary"
            title="Save Changes"
          >
            <Save size={18} />
            <span className="btn-text">{isSaving ? 'Saving...' : 'Save Changes'}</span>
          </button>
          <button
            onClick={handleSaveAndDownload}
            disabled={isSaving}
            className="btn btn-primary"
            title="Save & Download"
          >
            <Download size={18} />
            <span className="btn-text">Save & Download</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="editor-alert alert-error">
          {error}
        </div>
      )}

      {/* Optimization Suggestions Banner */}
      {showSuggestions && optimizationSuggestions.length > 0 && (
        <div className="optimization-banner">
          <div className="banner-header">
            <div className="banner-title">
              <span className="banner-icon">✨</span>
              <h3>AI Optimization Suggestions</h3>
            </div>
            <button
              onClick={() => setShowSuggestions(false)}
              className="banner-close"
            >
              ✕
            </button>
          </div>
          <div className="banner-content">
            <p className="banner-description">
              Based on the job description analysis, here are specific recommendations to improve your resume:
            </p>
            <ul className="suggestions-list">
              {optimizationSuggestions.map((suggestion, index) => (
                <li key={index} className="suggestion-item">
                  <span className="suggestion-number">{index + 1}</span>
                  <span className="suggestion-text">{suggestion}</span>
                </li>
              ))}
            </ul>
            <p className="banner-hint">
              Use the editor below to make these changes to your resume content.
            </p>
          </div>
        </div>
      )}

      {/* Mobile Tab Navigation */}
      <div className="mobile-tabs">
        <button
          className={`mobile-tab ${mobileActiveTab === 'edit' ? 'active' : ''}`}
          onClick={() => setMobileActiveTab('edit')}
        >
          <Edit3 size={18} />
          Edit
        </button>
        <button
          className={`mobile-tab ${mobileActiveTab === 'preview' ? 'active' : ''}`}
          onClick={() => setMobileActiveTab('preview')}
        >
          <Eye size={18} />
          Preview
        </button>
      </div>

      {/* Two-Pane Layout */}
      <div className="editor-content">
        {/* Left Sidebar: Editor Controls */}
        <div className={`editor-sidebar ${mobileActiveTab === 'edit' ? 'mobile-active' : 'mobile-hidden'}`}>
          {resumeData && (
            <>
              <SectionReorder
                customization={customization}
                onCustomizationChange={handleCustomizationChange}
              />
              <StyleCustomization
                customization={customization}
                onCustomizationChange={handleCustomizationChange}
              />
              <EditorSidebar
                resumeData={resumeData}
                customization={customization}
                onDataChange={handleDataChange}
                onCustomizationChange={handleCustomizationChange}
              />
            </>
          )}
        </div>

        {/* Right Pane: Live Preview */}
        <div className={`editor-preview-pane ${mobileActiveTab === 'preview' ? 'mobile-active' : 'mobile-hidden'}`}>
          <div className="preview-header desktop-only">
            <Eye size={18} />
            <span>Live Preview</span>
            {isPreviewLoading && <span className="preview-loading">Updating...</span>}
          </div>
          <LivePreview html={previewHTML} isLoading={isPreviewLoading} />
        </div>
      </div>
    </div>
  );
};

export default ResumeEditor;

